import { post } from "@src/appV2/api";
import { OPERATIONS_URL } from "@src/appV2/FacilityRules/api/useSaveFacilityRulesAndQuiz";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

const useSendWorkerInvitationsRequestSchema = z.object({
  "atomic:operations": z.array(
    z.object({
      op: z.literal("add"),
      data: z.object({
        type: z.literal("worker-invitation"),
        attributes: z.object({
          workplaceId: z.string(),
          recipient: z.object({
            name: z.string(),
            phone: z.string(),
            isPrnWorker: z.boolean().optional().default(false),
          }),
        }),
      }),
    })
  ),
});

const useSendWorkerInvitationsResponseSchema = z.object({
  "atomic:results": z.array(
    z.object({
      data: z.object({
        id: z.string(),
        type: z.literal("worker-invitation"),
      }),
    })
  ),
});

interface SendWorkerInvitationsParams {
  workplaceId: string;
  staffMembers: Array<{
    name: string;
    phone: string;
    isPrnWorker?: boolean;
  }>;
}

type AtomicOperations = z.infer<typeof useSendWorkerInvitationsRequestSchema>["atomic:operations"];

function getOperations(
  params: SendWorkerInvitationsParams
): z.infer<typeof useSendWorkerInvitationsRequestSchema> {
  const { workplaceId, staffMembers } = params;
  const operations: AtomicOperations = staffMembers.map(({ name, phone, isPrnWorker }) => ({
    op: "add",
    data: {
      type: "worker-invitation",
      attributes: {
        workplaceId,
        recipient: {
          name,
          phone,
          isPrnWorker: isPrnWorker ?? false,
        },
      },
    },
  }));

  return {
    "atomic:operations": operations,
  };
}

export function useSendWorkerInvitations(
  options: UseMutationOptions<void, string, SendWorkerInvitationsParams> = {}
) {
  return useMutation({
    mutationFn: async (params) => {
      const data = getOperations(params);

      if (data["atomic:operations"].length === 0) {
        return; // Nothing to update
      }

      await post({
        url: OPERATIONS_URL,
        data,
        requestSchema: useSendWorkerInvitationsRequestSchema,
        responseSchema: useSendWorkerInvitationsResponseSchema,
      });
    },
    ...options,
  });
}
